import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../services/api';
import {
  generateFullProgramSchedule,
  generateSinglePhaseSchedule,
  getScheduleTemplate,
  groupByDate,
} from '../../data/calendarSchedule';
import {
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
  Dumbbell,
  ClipboardCheck,
  CheckCircle,
  Clock,
  Check,
} from 'lucide-react';
import { Button } from '../../components/ui';

const DAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAYS_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const PHASE_COLORS = {
  1: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', dot: 'bg-blue-500' },
  2: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', dot: 'bg-green-500' },
  3: { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700', dot: 'bg-purple-500' },
  4: { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', dot: 'bg-orange-500' },
};

const PHASE_LABELS = { 1: 'I', 2: 'II', 3: 'III', 4: 'IV' };

export function CalendarPage() {
  const { user, aclCare } = useAuth();
  const navigate = useNavigate();
  
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('month'); // 'month' | 'week' | 'day'
  const [completedSessions, setCompletedSessions] = useState([]);
  const [completedTestOuts, setCompletedTestOuts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [phaseFilter, setPhaseFilter] = useState(null); // null = All, 1-4 = specific phase

  // Fetch completed sessions and test outs
  useEffect(() => {
    async function fetchData() {
      if (!user?.id) return;
      
      try {
        setLoading(true);
        
        const [sessionsResult, testOutsResult] = await Promise.all([
          api.getTrainingSessions(user.id),
          api.getTestOuts(user.id),
        ]);

        setCompletedSessions(sessionsResult.items || sessionsResult || []);
        setCompletedTestOuts(testOutsResult.items || testOutsResult || []);
      } catch (err) {
        console.error('[CalendarPage] Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [user?.id]);

  // Which phases are available for the filter bar
  const filterOptions = useMemo(() => {
    if (!aclCare) return [];
    const options = [];
    for (let p = 1; p <= 4; p++) {
      const pd = aclCare.phases?.[`phase_${p}`];
      const isUnlocked = p === 1 || pd?.unlocked === true;
      if (!isUnlocked) continue;
      options.push({
        phaseNum: p,
        label: PHASE_LABELS[p],
        isComplete: pd?.test_out_passed === true,
      });
    }
    return options;
  }, [aclCare]);

  // Generate schedule — branched on phase filter
  const scheduleItems = useMemo(() => {
    if (!aclCare) return [];

    if (phaseFilter === null) {
      return generateFullProgramSchedule(aclCare, completedSessions, completedTestOuts);
    }

    const phaseKey = `phase_${phaseFilter}`;
    const phaseData = aclCare.phases?.[phaseKey];
    const phaseSessions = completedSessions.filter(s => s.phase === phaseFilter);
    const phaseTestOuts = completedTestOuts.filter(t => t.phase === phaseFilter);
    return generateSinglePhaseSchedule(phaseFilter, phaseData, phaseSessions, phaseTestOuts);
  }, [aclCare, completedSessions, completedTestOuts, phaseFilter]);

  // Group items by date
  const itemsByDate = useMemo(() => {
    return groupByDate(scheduleItems);
  }, [scheduleItems]);

  // Progress banner data (only shown when a phase is filtered)
  const progressBanner = useMemo(() => {
    if (phaseFilter === null) return null;
    const phaseData = aclCare?.phases?.[`phase_${phaseFilter}`];
    if (!phaseData) return null;

    const template = getScheduleTemplate(phaseFilter);
    const totalSessions = template.filter(t => t.workout !== 'test_out').length;
    const completions = phaseData.completions || {};
    const completedCount = (completions.session_1 || 0) + (completions.session_2 || 0) + (completions.optional || 0);

    const phaseSessions = completedSessions
      .filter(s => s.phase === phaseFilter)
      .sort((a, b) => new Date(b.completed_at) - new Date(a.completed_at));

    const lastSessionDate = phaseSessions.length > 0
      ? new Date(phaseSessions[0].completed_at).toLocaleDateString('en-US', {
          month: 'long', day: 'numeric',
        })
      : null;

    return {
      phaseNum: phaseFilter,
      label: `Phase ${PHASE_LABELS[phaseFilter]}`,
      completedCount,
      totalSessions,
      lastSessionDate,
      isComplete: phaseData.test_out_passed === true,
    };
  }, [phaseFilter, aclCare, completedSessions]);

  const handlePhaseFilterChange = (newFilter) => {
    setPhaseFilter(newFilter);
    setCurrentDate(new Date());
  };

  // Navigation handlers
  const goToPrevious = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'month') {
      newDate.setMonth(newDate.getMonth() - 1);
    } else if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() - 7);
    } else {
      newDate.setDate(newDate.getDate() - 1);
    }
    setCurrentDate(newDate);
  };

  const goToNext = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'month') {
      newDate.setMonth(newDate.getMonth() + 1);
    } else if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() + 7);
    } else {
      newDate.setDate(newDate.getDate() + 1);
    }
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const handleEventClick = (item) => {
    if (item.isScheduled) {
      // Navigate to the training page for this phase
      navigate(`/program/phase/${item.phaseNum}`);
    }
    // For completed items, could show detail modal - for now just navigate
    if (item.isCompleted) {
      navigate(`/program/phase/${item.phaseNum}`);
    }
  };

  // Get title based on view mode
  const getTitle = () => {
    if (viewMode === 'month') {
      return `${MONTHS[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
    } else if (viewMode === 'week') {
      const startOfWeek = getStartOfWeek(currentDate);
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(endOfWeek.getDate() + 6);
      
      if (startOfWeek.getMonth() === endOfWeek.getMonth()) {
        return `${MONTHS[startOfWeek.getMonth()].slice(0, 3)} ${startOfWeek.getDate()}-${endOfWeek.getDate()}`;
      } else {
        return `${MONTHS[startOfWeek.getMonth()].slice(0, 3)} ${startOfWeek.getDate()} - ${MONTHS[endOfWeek.getMonth()].slice(0, 3)} ${endOfWeek.getDate()}`;
      }
    } else {
      return currentDate.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: 'numeric'
      });
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-4 pb-24 lg:pb-8">
      {/* Header with Legend */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
        <div className="flex items-center">
          <CalendarIcon className="h-6 w-6 text-primary mr-2" />
          <h1 className="text-2xl font-bold text-gray-900">Calendar</h1>
        </div>
        
        {/* Inline Legend */}
        <div className="flex items-center gap-3 text-xs">
          <div className="flex items-center gap-1">
            <div className="w-2.5 h-2.5 rounded-full bg-blue-500" />
            <span className="text-gray-600">I</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2.5 h-2.5 rounded-full bg-green-500" />
            <span className="text-gray-600">II</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2.5 h-2.5 rounded-full bg-purple-500" />
            <span className="text-gray-600">III</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2.5 h-2.5 rounded-full bg-orange-500" />
            <span className="text-gray-600">IV</span>
          </div>
          <div className="h-4 w-px bg-gray-300 mx-1" />
          <div className="flex items-center gap-1">
            <Clock className="w-3 h-3 text-gray-400" />
            <span className="text-gray-600">Scheduled</span>
          </div>
          <div className="flex items-center gap-1">
            <CheckCircle className="w-3 h-3 text-green-500" />
            <span className="text-gray-600">Done</span>
          </div>
        </div>
      </div>

      {/* Controls Bar - Responsive */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-3 mb-4">
        {/* Mobile: Stacked layout */}
        <div className="flex flex-col gap-3 sm:hidden">
          {/* Row 1: Navigation with title */}
          <div className="flex items-center justify-between">
            <button
              onClick={goToPrevious}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <ChevronLeft className="h-5 w-5 text-gray-600" />
            </button>
            
            <h2 className="text-base font-semibold text-gray-900 text-center">
              {getTitle()}
            </h2>
            
            <button
              onClick={goToNext}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <ChevronRight className="h-5 w-5 text-gray-600" />
            </button>
          </div>

          {/* Row 2: View toggle and Today button */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex rounded-lg border border-gray-200 overflow-hidden flex-1">
              {['month', 'week', 'day'].map((mode) => (
                <button
                  key={mode}
                  onClick={() => setViewMode(mode)}
                  className={`
                    flex-1 px-3 py-1.5 text-sm font-medium capitalize transition-colors
                    ${viewMode === mode 
                      ? 'bg-primary text-white' 
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                    }
                  `}
                >
                  {mode}
                </button>
              ))}
            </div>
            
            <Button variant="outline" size="sm" onClick={goToToday}>
              Today
            </Button>
          </div>
        </div>

        {/* Desktop: Single row layout */}
        <div className="hidden sm:flex items-center justify-between">
          {/* Navigation */}
          <div className="flex items-center gap-2">
            <button
              onClick={goToPrevious}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <ChevronLeft className="h-5 w-5 text-gray-600" />
            </button>
            
            <h2 className="text-lg font-semibold text-gray-900 min-w-[180px] text-center">
              {getTitle()}
            </h2>
            
            <button
              onClick={goToNext}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <ChevronRight className="h-5 w-5 text-gray-600" />
            </button>
          </div>

          {/* View Mode Toggle & Today Button */}
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={goToToday}>
              Today
            </Button>
            
            <div className="flex rounded-lg border border-gray-200 overflow-hidden">
              {['month', 'week', 'day'].map((mode) => (
                <button
                  key={mode}
                  onClick={() => setViewMode(mode)}
                  className={`
                    px-3 py-1.5 text-sm font-medium capitalize transition-colors
                    ${viewMode === mode 
                      ? 'bg-primary text-white' 
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                    }
                  `}
                >
                  {mode}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Phase Filter Bar */}
      {filterOptions.length > 1 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-3 mb-4">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-gray-500 mr-1">Phase:</span>
            <div className="flex rounded-lg border border-gray-200 overflow-hidden">
              <button
                onClick={() => handlePhaseFilterChange(null)}
                className={`
                  px-3 py-1.5 text-sm font-medium transition-colors
                  ${phaseFilter === null
                    ? 'bg-primary text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                  }
                `}
              >
                All
              </button>
              {filterOptions.map(({ phaseNum, label, isComplete }) => {
                const isSelected = phaseFilter === phaseNum;
                const colors = PHASE_COLORS[phaseNum];
                return (
                  <button
                    key={phaseNum}
                    onClick={() => handlePhaseFilterChange(phaseNum)}
                    className={`
                      px-3 py-1.5 text-sm font-medium transition-colors flex items-center gap-1
                      ${isSelected
                        ? `${colors.dot} text-white`
                        : isComplete
                          ? 'bg-white text-gray-400 hover:bg-gray-50'
                          : 'bg-white text-gray-600 hover:bg-gray-50'
                      }
                    `}
                  >
                    {isComplete && !isSelected && (
                      <Check className="w-3 h-3 text-green-500" />
                    )}
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Progress Banner */}
      {progressBanner && (
        <div className={`rounded-xl border-2 p-3 mb-4 flex items-center justify-between
          ${PHASE_COLORS[progressBanner.phaseNum].border}
          ${PHASE_COLORS[progressBanner.phaseNum].bg}`}
        >
          <div className="flex items-center gap-2">
            <div className={`w-2.5 h-2.5 rounded-full ${PHASE_COLORS[progressBanner.phaseNum].dot}`} />
            <span className={`text-sm font-semibold ${PHASE_COLORS[progressBanner.phaseNum].text}`}>
              {progressBanner.label}
            </span>
            <span className="text-sm text-gray-600">
              {progressBanner.isComplete
                ? 'Completed'
                : `· ${progressBanner.completedCount} of ${progressBanner.totalSessions} completed`
              }
            </span>
          </div>
          {progressBanner.lastSessionDate && (
            <span className="text-xs text-gray-500">
              Last session: {progressBanner.lastSessionDate}
            </span>
          )}
        </div>
      )}

      {/* Calendar Content */}
      {loading ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 flex items-center justify-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <>
          {viewMode === 'month' && (
            <MonthView 
              currentDate={currentDate} 
              itemsByDate={itemsByDate} 
              onEventClick={handleEventClick}
              onDayClick={(date) => {
                setCurrentDate(date);
                setViewMode('day');
              }}
            />
          )}
          {viewMode === 'week' && (
            <WeekView 
              currentDate={currentDate} 
              itemsByDate={itemsByDate} 
              onEventClick={handleEventClick}
              onDayClick={(date) => {
                setCurrentDate(date);
                setViewMode('day');
              }}
            />
          )}
          {viewMode === 'day' && (
            <DayView 
              currentDate={currentDate} 
              itemsByDate={itemsByDate} 
              onEventClick={handleEventClick}
            />
          )}
        </>
      )}
    </div>
  );
}

// ============ Month View ============
function MonthView({ currentDate, itemsByDate, onEventClick, onDayClick }) {
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  
  const firstDayOfMonth = new Date(year, month, 1);
  const lastDayOfMonth = new Date(year, month + 1, 0);
  const startingDay = firstDayOfMonth.getDay();
  const totalDays = lastDayOfMonth.getDate();

  const calendarDays = [];
  for (let i = 0; i < startingDay; i++) {
    calendarDays.push(null);
  }
  for (let day = 1; day <= totalDays; day++) {
    calendarDays.push(day);
  }

  const isToday = (day) => {
    const today = new Date();
    return day === today.getDate() && month === today.getMonth() && year === today.getFullYear();
  };

  const getDateString = (day) => {
    return new Date(year, month, day).toISOString().split('T')[0];
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      {/* Day headers */}
      <div className="grid grid-cols-7 border-b border-gray-200">
        {DAYS_SHORT.map(day => (
          <div key={day} className="text-center text-xs font-medium text-gray-500 py-3 bg-gray-50">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7">
        {calendarDays.map((day, index) => {
          if (day === null) {
            return <div key={`empty-${index}`} className="min-h-[80px] sm:min-h-[100px] border-b border-r border-gray-100 bg-gray-50/50" />;
          }

          const dateStr = getDateString(day);
          const dayItems = itemsByDate[dateStr] || [];
          const scheduledItems = dayItems.filter(i => i.isScheduled);
          const completedItems = dayItems.filter(i => i.isCompleted);

          return (
            <div
              key={day}
              className={`
                min-h-[80px] sm:min-h-[100px] border-b border-r border-gray-100 p-1 cursor-pointer
                hover:bg-gray-50 transition-colors
                ${isToday(day) ? 'bg-primary/5' : ''}
              `}
              onClick={() => onDayClick(new Date(year, month, day))}
            >
              {/* Day number */}
              <div className={`
                text-sm font-medium mb-1 w-6 h-6 sm:w-7 sm:h-7 flex items-center justify-center rounded-full
                ${isToday(day) ? 'bg-primary text-white' : 'text-gray-700'}
              `}>
                {day}
              </div>

              {/* Events - Show scheduled first (priority), then completed */}
              <div className="space-y-0.5 sm:space-y-1">
                {scheduledItems.slice(0, 2).map((item, idx) => (
                  <EventBlock 
                    key={`scheduled-${idx}`} 
                    item={item} 
                    onClick={(e) => {
                      e.stopPropagation();
                      onEventClick(item);
                    }}
                    compact
                  />
                ))}
                {completedItems.slice(0, 2 - scheduledItems.length).map((item, idx) => (
                  <EventBlock 
                    key={`completed-${idx}`} 
                    item={item} 
                    onClick={(e) => {
                      e.stopPropagation();
                      onEventClick(item);
                    }}
                    compact
                  />
                ))}
                {dayItems.length > 2 && (
                  <div className="text-xs text-gray-500 pl-1">
                    +{dayItems.length - 2}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============ Week View ============
function WeekView({ currentDate, itemsByDate, onEventClick, onDayClick }) {
  const startOfWeek = getStartOfWeek(currentDate);
  const weekDays = [];
  
  for (let i = 0; i < 7; i++) {
    const day = new Date(startOfWeek);
    day.setDate(day.getDate() + i);
    weekDays.push(day);
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="grid grid-cols-7 divide-x divide-gray-200">
        {weekDays.map((day, index) => {
          const dateStr = day.toISOString().split('T')[0];
          const dayItems = itemsByDate[dateStr] || [];
          const isToday = day.toDateString() === today.toDateString();
          const scheduledItems = dayItems.filter(i => i.isScheduled);
          const completedItems = dayItems.filter(i => i.isCompleted);

          return (
            <div key={index} className="min-h-[250px] sm:min-h-[300px]">
              {/* Day header */}
              <div 
                className={`
                  text-center py-2 sm:py-3 border-b border-gray-200 cursor-pointer hover:bg-gray-50
                  ${isToday ? 'bg-primary/5' : 'bg-gray-50'}
                `}
                onClick={() => onDayClick(day)}
              >
                <div className="text-xs font-medium text-gray-500">
                  {DAYS_SHORT[day.getDay()]}
                </div>
                <div className={`
                  text-base sm:text-lg font-semibold mt-1 w-7 h-7 sm:w-8 sm:h-8 mx-auto flex items-center justify-center rounded-full
                  ${isToday ? 'bg-primary text-white' : 'text-gray-900'}
                `}>
                  {day.getDate()}
                </div>
              </div>

              {/* Events */}
              <div className="p-1 sm:p-2 space-y-1 sm:space-y-2">
                {scheduledItems.map((item, idx) => (
                  <EventBlock 
                    key={`scheduled-${idx}`} 
                    item={item} 
                    onClick={() => onEventClick(item)}
                  />
                ))}
                {completedItems.map((item, idx) => (
                  <EventBlock 
                    key={`completed-${idx}`} 
                    item={item} 
                    onClick={() => onEventClick(item)}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============ Day View ============
function DayView({ currentDate, itemsByDate, onEventClick }) {
  const dateStr = currentDate.toISOString().split('T')[0];
  const dayItems = itemsByDate[dateStr] || [];
  const scheduledItems = dayItems.filter(i => i.isScheduled);
  const completedItems = dayItems.filter(i => i.isCompleted);

  const isToday = currentDate.toDateString() === new Date().toDateString();

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      {/* Day header */}
      <div className={`p-4 border-b border-gray-200 ${isToday ? 'bg-primary/5' : 'bg-gray-50'}`}>
        <div className="text-sm text-gray-500">{DAYS_FULL[currentDate.getDay()]}</div>
        <div className="text-2xl font-bold text-gray-900">
          {MONTHS[currentDate.getMonth()]} {currentDate.getDate()}, {currentDate.getFullYear()}
        </div>
        {isToday && (
          <span className="inline-block mt-2 text-xs px-2 py-1 bg-primary text-white rounded-full">
            Today
          </span>
        )}
      </div>

      {/* Events */}
      <div className="p-4">
        {dayItems.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <CalendarIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p className="font-medium">No sessions</p>
            <p className="text-sm">Nothing scheduled for this day</p>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Scheduled items first */}
            {scheduledItems.length > 0 && (
              <div>
                <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                  Scheduled
                </h3>
                <div className="space-y-2">
                  {scheduledItems.map((item, idx) => (
                    <EventBlock 
                      key={`scheduled-${idx}`} 
                      item={item} 
                      onClick={() => onEventClick(item)}
                      expanded
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Completed items */}
            {completedItems.length > 0 && (
              <div className={scheduledItems.length > 0 ? 'mt-4' : ''}>
                <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                  Completed
                </h3>
                <div className="space-y-2">
                  {completedItems.map((item, idx) => (
                    <EventBlock 
                      key={`completed-${idx}`} 
                      item={item} 
                      onClick={() => onEventClick(item)}
                      expanded
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ============ Event Block Component ============
function EventBlock({ item, onClick, compact = false, expanded = false }) {
  const colors = PHASE_COLORS[item.phaseNum] || PHASE_COLORS[1];
  const isTestOut = item.workout === 'test_out';

  if (compact) {
    // Compact view for month grid
    return (
      <button
        onClick={onClick}
        className={`
          w-full text-left px-1 py-0.5 rounded text-xs truncate transition-all
          ${item.isCompleted 
            ? 'bg-gray-100 text-gray-600 line-through opacity-70' 
            : `${colors.bg} ${colors.text} font-medium hover:opacity-80`
          }
        `}
      >
        <span className={`inline-block w-1.5 h-1.5 rounded-full mr-0.5 sm:mr-1 ${colors.dot}`} />
        <span className="hidden sm:inline">{item.label}</span>
        <span className="sm:hidden">{item.shortLabel || item.label.split(' ')[0]}</span>
      </button>
    );
  }

  if (expanded) {
    // Expanded view for day view
    return (
      <button
        onClick={onClick}
        className={`
          w-full text-left p-4 rounded-xl border-2 transition-all
          ${item.isCompleted 
            ? 'border-gray-200 bg-gray-50' 
            : `${colors.border} ${colors.bg} hover:shadow-md`
          }
        `}
      >
        <div className="flex items-start justify-between">
          <div className="flex items-center">
            <div className={`
              h-10 w-10 rounded-full flex items-center justify-center mr-3
              ${item.isCompleted ? 'bg-green-100' : colors.bg}
            `}>
              {isTestOut ? (
                <ClipboardCheck className={`h-5 w-5 ${item.isCompleted ? 'text-green-600' : colors.text}`} />
              ) : (
                <Dumbbell className={`h-5 w-5 ${item.isCompleted ? 'text-green-600' : colors.text}`} />
              )}
            </div>
            <div>
              <div className={`font-semibold ${item.isCompleted ? 'text-gray-600 line-through' : 'text-gray-900'}`}>
                {item.label}
              </div>
              <div className="text-sm text-gray-500">
                Phase {item.phaseNum}
                {item.rescheduled && ' • Rescheduled'}
              </div>
            </div>
          </div>

          {item.isCompleted ? (
            <CheckCircle className="h-5 w-5 text-green-500" />
          ) : (
            <ChevronRight className="h-5 w-5 text-gray-400" />
          )}
        </div>
      </button>
    );
  }

  // Default view for week grid
  return (
    <button
      onClick={onClick}
      className={`
        w-full text-left p-1.5 sm:p-2 rounded-lg border transition-all text-xs sm:text-sm
        ${item.isCompleted 
          ? 'border-gray-200 bg-gray-50 text-gray-500' 
          : `${colors.border} ${colors.bg} ${colors.text} hover:shadow-sm`
        }
      `}
    >
      <div className="flex items-center">
        {isTestOut ? (
          <ClipboardCheck className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-1 flex-shrink-0" />
        ) : (
          <Dumbbell className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-1 flex-shrink-0" />
        )}
        <span className={`truncate ${item.isCompleted ? 'line-through' : 'font-medium'}`}>
          {item.label}
        </span>
      </div>
      {item.isCompleted && (
        <div className="flex items-center mt-1 text-xs text-green-600">
          <CheckCircle className="h-3 w-3 mr-1" />
          Done
        </div>
      )}
    </button>
  );
}

// ============ Helper Functions ============
function getStartOfWeek(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day;
  return new Date(d.setDate(diff));
}